import { createContext, useContext, useCallback, type ReactNode } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api, ApiError } from "@/api/client";
import type { Me, LoginResult } from "@/api/types";

type AuthStatus = "loading" | "authenticated" | "unauthenticated";

interface AuthContextValue {
  me: Me | null;
  status: AuthStatus;
  login: (email: string, password: string) => Promise<LoginResult>;
  loginSupabase: (accessToken: string) => Promise<Me>;
  loginMfa: (code: string) => Promise<Me>;
  register: (input: { email: string; password: string; name?: string; orgName?: string; invite?: string }) => Promise<Me>;
  logout: () => Promise<void>;
  switchOrg: (orgId: string) => Promise<void>;
  refresh: () => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const qc = useQueryClient();

  const { data, status } = useQuery<Me | null>({
    queryKey: ["me"],
    queryFn: async () => {
      try {
        return await api.get<Me>("/api/auth/me");
      } catch (err) {
        if (err instanceof ApiError && err.status === 401) return null;
        throw err;
      }
    },
    retry: false,
    staleTime: 30_000,
  });

  const me = data ?? null;
  const authStatus: AuthStatus = status === "pending" ? "loading" : me ? "authenticated" : "unauthenticated";

  const login = useCallback(
    async (email: string, password: string) => {
      const result = await api.post<LoginResult>("/api/auth/login", { email, password });
      if (!result.mfaRequired) qc.setQueryData(["me"], result as Me);
      return result;
    },
    [qc],
  );

  const loginSupabase = useCallback(
    async (accessToken: string) => {
      const result = await api.post<Me>("/api/auth/login/supabase", { accessToken });
      qc.setQueryData(["me"], result);
      return result;
    },
    [qc],
  );

  const loginMfa = useCallback(
    async (code: string) => {
      const result = await api.post<Me>("/api/auth/login/mfa", { code });
      qc.setQueryData(["me"], result);
      return result;
    },
    [qc],
  );

  const register = useCallback(
    async (input: { email: string; password: string; name?: string; orgName?: string; invite?: string }) => {
      const result = await api.post<Me>("/api/auth/register", input);
      qc.setQueryData(["me"], result);
      return result;
    },
    [qc],
  );

  const logout = useCallback(async () => {
    await api.post("/api/auth/logout").catch(() => undefined);
    qc.setQueryData(["me"], null);
    qc.clear();
  }, [qc]);

  const switchOrg = useCallback(
    async (orgId: string) => {
      const result = await api.post<Me>("/api/auth/session/org", { orgId });
      qc.setQueryData(["me"], result);
      await qc.invalidateQueries();
    },
    [qc],
  );

  const refresh = useCallback(() => {
    void qc.invalidateQueries({ queryKey: ["me"] });
  }, [qc]);

  return (
    <AuthContext.Provider
      value={{ me, status: authStatus, login, loginSupabase, loginMfa, register, logout, switchOrg, refresh }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within <AuthProvider>");
  return ctx;
}
